import {createStylesheetManager} from '@gl/app/core/stylesheet_manager.js';
import {ignite} from '@gl/app/kiln/index.js';
import {
  addRumContextFromSessionWhenReady,
  initDataDog,
} from '@gl/app/lib/datadog.js';
import {initMixpanelFromSessionWhenReady} from '@gl/app/lib/mixpanel.js';
import {App} from '@gl/app/lit/lib/app.js';
import {registerBundleLoader} from '@gl/app/lit/lib/bundle_loader.js';
import {FlywheelComponent} from '@gl/app/lit/lib/component.js';
import {initChunkLoadErrorHandler} from '@gl/app/utils/chunk_load_error.js';
import {isDev} from '@gl/app/utils/general.js';
import {installLogoutBfcacheGuard} from '@gl/app/utils/logout_bfcache.js';

import {routes} from './routes/index.js';

import './layer_order.css';
import './normalize_legacy.css';
import '@gl/app/lit/common/styles/app.scss';
import '@gl/app/ui/global.scss';
import '@gl/app/styles/app.scss';
import '@gl/app/core/root.js';

// Create an async split point for the theme definition
import('@gl/app/ui/design_system/theme.css.js');

const app = new App({
  name: 'VortexFrontend',
  routes,
});

if (isDev) {
  // Enable HMR for Flywheel components.
  import('@gl/app/utils/hmr.js');
}

// Initialize stylesheet manager for adoptedStyleSheets
const stylesheetManager = createStylesheetManager();

if (
  new URLSearchParams(window.location.search).has('perf') ||
  localStorage.getItem('gl:perf')
) {
  import('@gl/app/features/performance/init.js').then(({init}) => {
    init();
  });
}

const shouldEnableLocalMocks = (): boolean => {
  try {
    return localStorage.getItem('gl:msw') === 'true';
  } catch {
    return false;
  }
};

initChunkLoadErrorHandler();

const bootstrap = async () => {
  if (shouldEnableLocalMocks()) {
    try {
      const {startMockServer} = await import('../../msw/server.js');
      await startMockServer();
    } catch (error) {
      console.error('[MSW] Failed to start local mock server', error);
    }
  }

  app.init();

  registerBundleLoader(pathname => app.loadComponentsForPath(pathname));

  installLogoutBfcacheGuard();
};

bootstrap();

addEventListener('DOMContentLoaded', () => {
  stylesheetManager.init();
  // Initiates Kiln.
  ignite(FlywheelComponent.tagRegistry);
  // Initialize Datadog RUM early so web vitals like first paint / LCP are captured (not blocked on session).
  initDataDog();
  // Add session context to RUM after session provider has hydrated session store.
  addRumContextFromSessionWhenReady();
  // Initializes Mixpanel after session provider has hydrated session store.
  initMixpanelFromSessionWhenReady();
});

if (module.hot) {
  module.hot.accept(error => {
    if (error) {
      console.error('HMR error occurred:', error);
      return;
    }

    console.log('HMR: Module updated successfully');
  });
}
